import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  GetResourceSearchResultsItem,
  GetResourceSearchResultsRequest,
  ResourceTypeFacet,
} from "@/api/getResourceSearchResults/types";
import { AppDispatch, AppThunk } from "@/app/store";
import { resourceLibraryAlgoliaService } from "@/modules/hub/features/ResourceLibrary/services/resourceLibraryAlgoliaService";
import {
  Trade,
  BusinessRole,
  ProcessWheelFacet,
} from "@/modules/hub/features/ResourceLibrary/api/getResourceLibraryMetadata/types";
import { ResourceCategory } from "@/modules/hub/features/ResourceLibrary/api/getResourceLibraryMetadata/types";

interface ResourceLibraryState {
  resources: GetResourceSearchResultsItem[];
  resourceTypes: ResourceTypeFacet[];
  page: number;
  hasMore: boolean;
  totalCount: number;
  searchInput: string;
  showOnlyFavorites: boolean;
  showOnlyFeatured: boolean;
  showOnlySharedWithMe: boolean;
  selectedResourceType: number | null;
  selectedTag: string | null;
  scrollPosition: number;
  isRestoringState: boolean;
  isFetching: boolean;
  pendingRequest: "search" | "pagination" | null;
  lastFetchParams: {
    searchTerm?: string;
    showFavorites?: boolean;
    showFeatured?: boolean;
    showSharedWithMe?: boolean;
    resourceTypeIds?: number[];
    tradeIds?: number[];
    resourceCategoryIds?: number[];
    businessRoleIds?: number[];
    page?: number;
    pageSize?: number;
  } | null;

  selectedResourceTypes: ResourceTypeFacet[];
  selectedTrades: Trade[];
  selectedResourceCategories: ResourceCategory[];
  selectedBusinessRoles: BusinessRole[];
  selectedProcessWheels: ProcessWheelFacet[];

  // Dynamic facets from Algolia with counts
  availableFacets: {
    resourceTypes: Array<{
      id: number;
      name: string;
      resourceCount: number;
      icon: string | null;
    }>;
    categories: Array<{ id: number; name: string; count: number }>;
    businessRoles: Array<{
      id: number;
      name: string;
      description: string | null;
      count: number;
    }>;
    trades: Array<{
      id: number;
      name: string;
      icon: string | null;
      count: number;
    }>;
    processWheels: Array<{ id: number; name: string; count: number }>;
  } | null;
}

const initialState: ResourceLibraryState = {
  resources: [],
  resourceTypes: [],
  page: 1,
  hasMore: true,
  totalCount: 0,
  searchInput: "",
  showOnlyFavorites: false,
  showOnlyFeatured: false,
  showOnlySharedWithMe: false,
  selectedResourceType: null,
  selectedTag: null,
  scrollPosition: 0,
  isRestoringState: false,
  isFetching: false,
  pendingRequest: null,
  lastFetchParams: null,
  selectedResourceTypes: [],
  selectedTrades: [],
  selectedResourceCategories: [],
  selectedBusinessRoles: [],
  selectedProcessWheels: [],
  availableFacets: null,
};

const resourceLibrarySlice = createSlice({
  name: "resourceLibrary",
  initialState,
  reducers: {
    setResources: (
      state,
      action: PayloadAction<GetResourceSearchResultsItem[]>,
    ) => {
      state.resources = action.payload;
    },
    appendResources: (
      state,
      action: PayloadAction<GetResourceSearchResultsItem[]>,
    ) => {
      // Filter out duplicates based on resource ID
      const existingIds = new Set(state.resources.map((r) => r.id));
      const newResources = action.payload.filter((r) => !existingIds.has(r.id));
      state.resources = [...state.resources, ...newResources];
    },
    setResourceTypes: (state, action: PayloadAction<ResourceTypeFacet[]>) => {
      state.resourceTypes = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },
    setSearchInput: (state, action: PayloadAction<string>) => {
      state.searchInput = action.payload;
    },
    setShowOnlyFavorites: (state, action: PayloadAction<boolean>) => {
      state.showOnlyFavorites = action.payload;
    },
    setShowOnlyFeatured: (state, action: PayloadAction<boolean>) => {
      state.showOnlyFeatured = action.payload;
    },
    setShowOnlySharedWithMe: (state, action: PayloadAction<boolean>) => {
      state.showOnlySharedWithMe = action.payload;
    },
    setScrollPosition: (state, action: PayloadAction<number>) => {
      state.scrollPosition = action.payload;
    },
    setIsRestoringState: (state, action: PayloadAction<boolean>) => {
      state.isRestoringState = action.payload;
    },
    setLastFetchParams: (
      state,
      action: PayloadAction<ResourceLibraryState["lastFetchParams"]>,
    ) => {
      state.lastFetchParams = action.payload;
    },
    setSelectedResourceTypes: (
      state,
      action: PayloadAction<ResourceTypeFacet[]>,
    ) => {
      state.selectedResourceTypes = action.payload;
    },
    setSelectedTrades: (state, action: PayloadAction<Trade[]>) => {
      state.selectedTrades = action.payload;
    },
    setSelectedResourceCategories: (
      state,
      action: PayloadAction<ResourceCategory[]>,
    ) => {
      state.selectedResourceCategories = action.payload;
    },
    setSelectedBusinessRoles: (
      state,
      action: PayloadAction<BusinessRole[]>,
    ) => {
      state.selectedBusinessRoles = action.payload;
    },
    setSelectedProcessWheels: (
      state,
      action: PayloadAction<ProcessWheelFacet[]>,
    ) => {
      state.selectedProcessWheels = action.payload;
    },
    setIsFetching: (state, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload;
    },
    setPendingRequest: (
      state,
      action: PayloadAction<"search" | "pagination" | null>,
    ) => {
      state.pendingRequest = action.payload;
    },
    setAvailableFacets: (
      state,
      action: PayloadAction<ResourceLibraryState["availableFacets"]>,
    ) => {
      state.availableFacets = action.payload;
    },
    resetState: (state) => {
      Object.assign(state, initialState);
    },
    clearFilters: (state) => {
      state.searchInput = "";
      state.showOnlyFavorites = false;
      state.showOnlyFeatured = false;
      state.showOnlySharedWithMe = false;
      state.selectedResourceType = null;
      state.selectedTag = null;
      state.selectedResourceTypes = [];
      state.selectedTrades = [];
      state.selectedResourceCategories = [];
      state.selectedBusinessRoles = [];
      state.selectedProcessWheels = [];
      state.totalCount = 0;
    },
    hydrateFromCache: (
      state,
      action: PayloadAction<Partial<ResourceLibraryState>>,
    ) => {
      const cachedState = action.payload;

      if (cachedState.resources) {
        state.resources = cachedState.resources;
      }
      if (cachedState.page) {
        state.page = cachedState.page;
      }
      if (typeof cachedState.hasMore === "boolean") {
        state.hasMore = cachedState.hasMore;
      }
      if (cachedState.scrollPosition) {
        state.scrollPosition = cachedState.scrollPosition;
      }
      if (cachedState.lastFetchParams) {
        state.lastFetchParams = cachedState.lastFetchParams;
      }
      if (cachedState.searchInput !== undefined) {
        state.searchInput = cachedState.searchInput;
      }
      if (typeof cachedState.showOnlyFavorites === "boolean") {
        state.showOnlyFavorites = cachedState.showOnlyFavorites;
      }
      if (typeof cachedState.showOnlyFeatured === "boolean") {
        state.showOnlyFeatured = cachedState.showOnlyFeatured;
      }
      if (typeof cachedState.showOnlySharedWithMe === "boolean") {
        state.showOnlySharedWithMe = cachedState.showOnlySharedWithMe;
      }
      if (cachedState.selectedResourceType !== undefined) {
        state.selectedResourceType = cachedState.selectedResourceType;
      }
      if (typeof cachedState.totalCount === "number") {
        state.totalCount = cachedState.totalCount;
      }
    },
  },
});

// Promise queue to ensure requests run sequentially
let currentRequest: Promise<void> | null = null;

export const fetchResources =
  (params: GetResourceSearchResultsRequest): AppThunk =>
  async (dispatch: AppDispatch, getState) => {
    // Wait for any current request to complete
    if (currentRequest) {
      await currentRequest;
    }

    // If there's a pending pagination request, skip it since we're doing a fresh search
    const state = getState();
    if (state.resourceLibrary.pendingRequest === "pagination") {
      dispatch(setPendingRequest(null));
      return;
    }

    dispatch(setIsFetching(true));

    // Create new promise for this request
    currentRequest = (async () => {
      try {
        // Use Algolia instead of backend API
        const response =
          await resourceLibraryAlgoliaService.searchResources(params);

        dispatch(setResources(response.resources || []));
        dispatch(setPage(params.page || 1));
        dispatch(setLastFetchParams(params));

        // Set the dynamic facets from Algolia
        dispatch(setAvailableFacets(response.facets));

        // Set total count from Algolia response
        if (response.totalCount !== undefined) {
          dispatch(setTotalCount(response.totalCount));
        }

        const hasMore = response.resources.length >= (params.pageSize || 10);
        dispatch(setHasMore(hasMore));
      } catch (error) {
        console.error("Error fetching resources from Algolia:", error);
        dispatch(setHasMore(false));
      } finally {
        dispatch(setIsFetching(false));
        currentRequest = null;
      }
    })();

    await currentRequest;
  };

export const fetchMoreResources =
  (params: GetResourceSearchResultsRequest): AppThunk =>
  async (dispatch: AppDispatch, getState) => {
    const state = getState();

    // Don't paginate if we're already fetching
    if (state.resourceLibrary.isFetching) {
      // Queue this as a pending pagination request
      dispatch(setPendingRequest("pagination"));
      return;
    }

    // Wait for any current request to complete
    if (currentRequest) {
      await currentRequest;
    }

    dispatch(setIsFetching(true));
    dispatch(setPendingRequest(null));

    // Create new promise for this request
    currentRequest = (async () => {
      try {
        // Use Algolia instead of backend API
        console.log("[ResourceLibrary] Fetching more with params:", params);
        const response =
          await resourceLibraryAlgoliaService.searchResources(params);
        console.log(
          "[ResourceLibrary] Algolia returned:",
          response.resources.length,
          "resources, total:",
          response.totalCount,
        );
        console.log(
          "[ResourceLibrary] Pagination Algolia facets:",
          response.facets,
        );

        dispatch(appendResources(response.resources || []));
        dispatch(setPage(params.page || 1));
        dispatch(setLastFetchParams(params));

        // Update facets even during pagination for fresh counts
        dispatch(setAvailableFacets(response.facets));

        // Update total count if provided (should remain same for pagination)
        if (response.totalCount !== undefined) {
          dispatch(setTotalCount(response.totalCount));
        }

        const hasMore = response.resources.length >= (params.pageSize || 10);
        dispatch(setHasMore(hasMore));
      } catch (error) {
        console.error("Error loading more resources from Algolia:", error);
        dispatch(setHasMore(false));
      } finally {
        dispatch(setIsFetching(false));
        currentRequest = null;

        // Check if there's a pending request after we're done
        const afterState = getState();
        if (afterState.resourceLibrary.pendingRequest === "pagination") {
          // Trigger the pending pagination
          dispatch(setPendingRequest(null));
          const nextPage = afterState.resourceLibrary.page + 1;
          dispatch(fetchMoreResources({ ...params, page: nextPage }));
        }
      }
    })();

    await currentRequest;
  };

export const {
  setResources,
  appendResources,
  setPage,
  setHasMore,
  setTotalCount,
  setSearchInput,
  setShowOnlyFavorites,
  setShowOnlyFeatured,
  setShowOnlySharedWithMe,
  setScrollPosition,
  setLastFetchParams,
  setSelectedResourceTypes,
  setSelectedTrades,
  setSelectedResourceCategories,
  setSelectedBusinessRoles,
  setSelectedProcessWheels,
  setIsRestoringState,
  setIsFetching,
  setPendingRequest,
  setAvailableFacets,
  clearFilters,
} = resourceLibrarySlice.actions;

export default resourceLibrarySlice.reducer;
